import { useCallback, useEffect, useState } from 'react';

/**
 * Shared sidebar-collapsed state for `AppShell` + `TopBar`.
 *
 * Both the Drawer (renders width) and the TopBar toggle button consume
 * the same value, so the simplest cross-component sync is a hook that
 * also persists to `localStorage` (so a returning user lands on their
 * preferred width).
 *
 * Co-locates a tiny `window` event bridge so multiple hook instances
 * in the same page stay in lock-step without us reaching for a
 * Context Provider just for one boolean.
 */
const STORAGE_KEY = 'qfly.sidebar.collapsed';
const EVENT_NAME = 'qfly:sidebar-collapsed-changed';

const readPersisted = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writePersisted = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable / quota — best-effort persistence.
  }
};

export interface SidebarState {
  readonly collapsed: boolean;
  readonly toggle: () => void;
  readonly setCollapsed: (next: boolean) => void;
}

export const useSidebarCollapsed = (): SidebarState => {
  const [collapsed, setCollapsedState] = useState<boolean>(readPersisted);

  // Subscribe to the cross-instance broadcast so both `AppShell` and
  // `TopBar` flip together when either one calls `toggle()`.
  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<boolean>).detail;
      setCollapsedState(Boolean(detail));
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const setCollapsed = useCallback((next: boolean): void => {
    setCollapsedState(next);
    writePersisted(next);
    window.dispatchEvent(new CustomEvent<boolean>(EVENT_NAME, { detail: next }));
  }, []);

  const toggle = useCallback((): void => {
    setCollapsed(!readPersisted());
  }, [setCollapsed]);

  return { collapsed, toggle, setCollapsed };
};
